import { useState } from "react";
import {
  ActionMenu, Button, Confirm, Empty, EnvBadge, ErrorNotice, Field, InfoTip, Input, Loading, Modal, Money, Panel, SecretReveal, Segmented, useToast,
} from "../../components/ui";
import { gameApi, type GameApi } from "../../lib/api";
import { invalidate, useAction, useQuery } from "../../lib/query";
import { useClient } from "../../lib/session";
import { formatDateTime, relativeTime, untilText } from "../../lib/time";
import type { AccountAlert, Env, WebhookDelivery, WebhookEndpoint, WebhookEvent, WebhookEventType } from "../../lib/types";
import type { GameTabProps } from "./Game";

const EVENT_TYPES: { value: WebhookEventType; label: string; help: string }[] = [
  { value: "account.debited", label: "Debit alerts", help: "Money left a player's account: transfers, purchases, fees, reversals." },
  { value: "account.credited", label: "Credit alerts", help: "Money reached a player's account: funding, incoming transfers, refunds." },
];

const isAlert = (e: WebhookEvent): e is WebhookEvent & { data: AccountAlert } => e.type === "account.debited" || e.type === "account.credited";

/** "Delivered 2 min ago", "Failing since 3 h ago", "No deliveries yet". */
export function endpointHealth(ep: Pick<WebhookEndpoint, "status" | "disabled_reason" | "failing_since" | "last_success_at">): { tone: string; label: string; detail: string } {
  if (ep.status === "disabled") return { tone: "bad", label: "Disabled", detail: ep.disabled_reason ?? "" };
  if (ep.failing_since) return { tone: "bad", label: "Failing", detail: `since ${relativeTime(ep.failing_since)}` };
  if (ep.last_success_at) return { tone: "ok", label: "Healthy", detail: `delivered ${relativeTime(ep.last_success_at)}` };
  return { tone: "", label: "Active", detail: "no deliveries yet" };
}

/** A delivery's outcome in a few words: "Delivered · 204 in 38 ms", "Retrying · 500 · attempt 2". */
export function deliveryText(d: Pick<WebhookDelivery, "state" | "attempts" | "last_status" | "last_error" | "last_response_ms">): string {
  const answer = d.last_status ? `${d.last_status}${d.last_response_ms != null ? ` in ${d.last_response_ms} ms` : ""}` : d.last_error ?? "";
  switch (d.state) {
    case "delivered": return `Delivered · ${answer}`;
    case "failed": return `Failed after ${d.attempts} attempt${d.attempts === 1 ? "" : "s"}${answer ? ` · ${answer}` : ""}`;
    case "pending": return d.attempts === 0 ? "Queued" : `Retrying · ${answer} · attempt ${d.attempts}`;
    default: return "Sending…";
  }
}

export function GameWebhooks({ game, env }: GameTabProps) {
  return (
    <div className="stack">
      <Endpoints gameId={game.id} env={env} />
      <EventLog gameId={game.id} env={env} />
    </div>
  );
}

function Endpoints({ gameId, env }: { gameId: string; env: Env }) {
  const api = gameApi(useClient(), { gameId, env });
  const toast = useToast();
  const key = `dev:webhooks:${gameId}:${env}`;
  const endpoints = useQuery(key, () => api.webhookEndpoints());
  const [editing, setEditing] = useState<WebhookEndpoint | "new" | null>(null);
  const [revealed, setRevealed] = useState<{ title: string; secret: string } | null>(null);
  const [rotating, setRotating] = useState<WebhookEndpoint | null>(null);
  const [deleting, setDeleting] = useState<WebhookEndpoint | null>(null);
  const test = useAction((ep: WebhookEndpoint) => api.testWebhookEndpoint(ep.id));
  const toggle = useAction((ep: WebhookEndpoint) => api.updateWebhookEndpoint(ep.id, { status: ep.status === "active" ? "disabled" : "active" }));
  const rotate = useAction((ep: WebhookEndpoint) => api.rotateWebhookSecret(ep.id));
  const remove = useAction(async (ep: WebhookEndpoint) => {
    await api.deleteWebhookEndpoint(ep.id);
    return true;
  });
  const rows = endpoints.data?.data ?? [];

  const sendTest = async (ep: WebhookEndpoint) => {
    const d = await test.run(ep);
    invalidate(key);
    invalidate(`dev:webhook-events:${gameId}:${env}`);
    if (d) toast(d.state === "delivered" ? `Test event ${deliveryText(d).toLowerCase()}` : `Test event not delivered: ${d.last_error ?? d.last_status}`);
  };

  return (
    <Panel
      title={<>Webhook endpoints <EnvBadge env={env} /></>}
      sub={<>Every money movement on a player's account sends a signed debit or credit alert to these URLs. Verify the <code>GBE-Signature</code> header with your SDK's <code>verifyWebhook</code> before trusting a request.</>}
      actions={<Button variant="primary" size="small" onClick={() => setEditing("new")} disabled={rows.length >= 10}>Add endpoint</Button>}
      flush
    >
      <ErrorNotice error={endpoints.error ?? test.error ?? toggle.error} onRetry={endpoints.reload} />
      {endpoints.loading && !endpoints.data && <Loading />}
      {endpoints.data && rows.length === 0 && (
        <Empty title="No endpoints yet" action={<Button variant="primary" onClick={() => setEditing("new")}>Add endpoint</Button>}>
          Add your game server's URL to receive debit and credit alerts as they happen. {env === "sandbox" ? "The sandbox accepts http and https" : "Production needs https"}; the engine
          can't reach <code>localhost</code>, so use a tunnel such as cloudflared or ngrok while developing.
        </Empty>
      )}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="data">
            <thead><tr><th>Endpoint</th><th>Events</th><th>Health</th><th>Secret</th><th /></tr></thead>
            <tbody>
              {rows.map((ep) => {
                const h = endpointHealth(ep);
                return (
                  <tr key={ep.id}>
                    <td style={{ maxWidth: 340 }}>
                      <div className="cell-title truncate mono" title={ep.url}>{ep.url}</div>
                      {ep.description && <div className="cell-sub">{ep.description}</div>}
                    </td>
                    <td>
                      <div className="scope-list">
                        {ep.event_types.map((t) => <code key={t}>{t === "*" ? "all events" : t}</code>)}
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${h.tone}`}>{h.label}</span>
                      {h.detail && <div className="cell-sub">{h.detail}</div>}
                    </td>
                    <td>
                      <span className="mono small">{ep.secret_prefix}…</span>
                      {ep.previous_secret_expires_at && <div className="cell-sub">old secret valid until {formatDateTime(ep.previous_secret_expires_at)}</div>}
                    </td>
                    <td className="num">
                      <ActionMenu
                        label={`Actions for ${ep.url}`}
                        items={[
                          ...(ep.status === "active" ? [{ label: "Send test event", onSelect: () => void sendTest(ep) }] : []),
                          { label: "Edit", onSelect: () => setEditing(ep) },
                          {
                            label: ep.status === "active" ? "Pause" : "Enable",
                            onSelect: async () => {
                              if (await toggle.run(ep)) {
                                invalidate(key);
                                toast(ep.status === "active" ? "Endpoint paused" : "Endpoint enabled");
                              }
                            },
                          },
                          { label: "Rotate secret", onSelect: () => setRotating(ep) },
                          { label: "Delete", danger: true, onSelect: () => setDeleting(ep) },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EndpointModal
          api={api}
          env={env}
          endpoint={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(ep) => {
            setEditing(null);
            invalidate(key);
            if (ep.secret) setRevealed({ title: "Endpoint added", secret: ep.secret });
            else toast("Endpoint saved");
          }}
        />
      )}
      <Modal open={!!revealed} title={revealed?.title ?? ""} onClose={() => setRevealed(null)}
        description="Your server checks each delivery's GBE-Signature with this secret. Store it as a secret on your game server."
        footer={<Button variant="primary" onClick={() => setRevealed(null)}>Done</Button>}>
        {revealed && <SecretReveal secret={revealed.secret} what="signing secret" />}
      </Modal>
      <Confirm
        open={!!rotating}
        title="Rotate the signing secret?"
        body={<>You get a new secret now. For 24 hours each delivery is signed with both the new and the old secret, so you can deploy the new one without dropping events.</>}
        confirmLabel="Rotate secret"
        busy={rotate.pending}
        error={rotate.error}
        onConfirm={async () => {
          const ep = rotating && (await rotate.run(rotating));
          if (ep) {
            setRotating(null);
            invalidate(key);
            setRevealed({ title: "New signing secret", secret: ep.secret ?? "" });
          }
        }}
        onClose={() => setRotating(null)}
      />
      <Confirm
        open={!!deleting}
        title="Delete this endpoint?"
        body={<>Deliveries to <span className="mono">{deleting?.url}</span> stop now, including queued retries. To stop briefly, pause it instead.</>}
        confirmLabel="Delete endpoint"
        danger
        busy={remove.pending}
        error={remove.error}
        onConfirm={async () => {
          if (deleting && (await remove.run(deleting))) {
            setDeleting(null);
            invalidate(key);
            toast("Endpoint deleted");
          }
        }}
        onClose={() => setDeleting(null)}
      />
    </Panel>
  );
}

function EndpointModal({ api, env, endpoint, onClose, onSaved }: {
  api: GameApi;
  env: Env;
  endpoint: WebhookEndpoint | null;
  onClose: () => void;
  onSaved: (ep: WebhookEndpoint) => void;
}) {
  const [url, setUrl] = useState(endpoint?.url ?? "");
  const [description, setDescription] = useState(endpoint?.description ?? "");
  const initial = endpoint?.event_types ?? ["*"];
  const [types, setTypes] = useState<WebhookEventType[]>(initial.includes("*") ? EVENT_TYPES.map((t) => t.value) : (initial as WebhookEventType[]));
  const save = useAction(() => {
    const event_types = types.length === EVENT_TYPES.length ? (["*"] as const).slice() : types;
    const body = { url: url.trim(), description: description.trim(), event_types };
    return endpoint ? api.updateWebhookEndpoint(endpoint.id, body) : api.createWebhookEndpoint(body);
  });
  const toggle = (t: WebhookEventType) => setTypes((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));

  return (
    <Modal
      open
      title={endpoint ? "Edit endpoint" : "Add a webhook endpoint"}
      description={env === "production" ? "Production endpoints must use https." : "Sandbox endpoints may use http or https."}
      onClose={onClose}
      onSubmit={async (e) => {
        e.preventDefault();
        const ep = await save.run();
        if (ep) onSaved(ep);
      }}
      footer={<>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="primary" busy={save.pending} disabled={!url.trim() || types.length === 0}>{endpoint ? "Save" : "Add endpoint"}</Button>
      </>}
    >
      <div className="form">
        <Field label="URL" hint="The engine POSTs JSON here and expects a 2xx answer within 10 seconds. Localhost and private addresses are refused; use a tunnel while developing.">
          <Input type="url" required value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://game.example.com/gbe/webhooks" maxLength={2048} autoFocus />
        </Field>
        <Field label="Description" hint="Optional, for your team.">
          <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Game server alerts" maxLength={200} />
        </Field>
        <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
          <legend className="small" style={{ fontWeight: 500, marginBottom: 8 }}>Events</legend>
          <div className="stack" style={{ gap: 8 }}>
            {EVENT_TYPES.map((t) => (
              <label key={t.value} className="row" style={{ alignItems: "flex-start", gap: 8 }}>
                <input type="checkbox" checked={types.includes(t.value)} onChange={() => toggle(t.value)} style={{ marginTop: 4 }} />
                <span>
                  {t.label} <code className="mono small">{t.value}</code>
                  <span className="cell-sub" style={{ display: "block" }}>{t.help}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        <ErrorNotice error={save.error} />
      </div>
    </Modal>
  );
}

type Filter = "all" | WebhookEventType | "webhook.test";

function EventLog({ gameId, env }: { gameId: string; env: Env }) {
  const api = gameApi(useClient(), { gameId, env });
  const [filter, setFilter] = useState<Filter>("all");
  const type = filter === "all" ? undefined : filter;
  const first = useQuery(`dev:webhook-events:${gameId}:${env}:${filter}`, () => api.webhookEvents({ type, limit: 25 }), { refreshMs: 5000 });
  const [older, setOlder] = useState<{ filter: Filter; events: WebhookEvent[]; cursor: string | null } | null>(null);
  const more = useAction((cursor: string) => api.webhookEvents({ type, limit: 25, cursor }));
  const [open, setOpen] = useState<WebhookEvent | null>(null);

  const extra = older?.filter === filter ? older : null;
  const events = [...(first.data?.data ?? []), ...(extra?.events ?? [])];
  const cursor = extra ? extra.cursor : first.data?.next_cursor ?? null;

  return (
    <Panel
      title={<>Events <InfoTip label="About the event log">The last 30 days of events, newest first, whether or not an endpoint received them. Your server can poll this feed too (<code>GET /v1/webhooks/events</code>).</InfoTip></>}
      sub="Debit and credit alerts as they were sent. Open one to see its deliveries and send it again."
      actions={
        <Segmented<Filter>
          label="Event type"
          value={filter}
          onChange={setFilter}
          options={[{ value: "all", label: "All" }, { value: "account.debited", label: "Debits" }, { value: "account.credited", label: "Credits" }, { value: "webhook.test", label: "Tests" }]}
        />
      }
      flush
    >
      <ErrorNotice error={first.error ?? more.error} onRetry={first.reload} />
      {first.loading && !first.data && <Loading />}
      {first.data && events.length === 0 && (
        <Empty title="No events yet">Move money in the sandbox playground and its alerts appear here.</Empty>
      )}
      {events.length > 0 && (
        <div className="table-wrap">
          <table className="data">
            <thead><tr><th>When</th><th>Event</th><th>Account</th><th className="num">Amount</th><th>Details</th><th /></tr></thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id}>
                  <td title={formatDateTime(e.created_at)}>{relativeTime(e.created_at)}</td>
                  <td><code className="mono small">{e.type}</code></td>
                  {isAlert(e) ? (
                    <>
                      <td><div className="cell-title">{e.data.account_name}</div><div className="cell-sub mono">{e.data.account_number}</div></td>
                      <td className="num">
                        <Money minor={e.type === "account.debited" ? -e.data.total_minor : e.data.total_minor} currency={e.data.currency} signed
                          tone={e.type === "account.debited" ? "debit" : "credit"} />
                        {e.data.charges.length > 0 && <div className="cell-sub">incl. {e.data.charges.length} charge{e.data.charges.length === 1 ? "" : "s"}</div>}
                      </td>
                      <td>
                        <div>{e.data.narration ?? e.data.transaction_type}</div>
                        {e.data.counterparty && <div className="cell-sub">{e.type === "account.debited" ? "to" : "from"} {e.data.counterparty.account_name}</div>}
                      </td>
                    </>
                  ) : (
                    <td colSpan={3} className="cell-sub">Test event</td>
                  )}
                  <td className="num"><Button size="small" onClick={() => setOpen(e)}>Deliveries</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {cursor && (
        <div className="pager">
          <Button size="small" busy={more.pending} onClick={async () => {
            const page = await more.run(cursor);
            if (page) setOlder({ filter, events: [...(extra?.events ?? []), ...page.data], cursor: page.next_cursor });
          }}>Load older events</Button>
        </div>
      )}
      {open && <EventModal api={api} gameId={gameId} env={env} event={open} onClose={() => setOpen(null)} />}
    </Panel>
  );
}

function EventModal({ api, gameId, env, event, onClose }: { api: GameApi; gameId: string; env: Env; event: WebhookEvent; onClose: () => void }) {
  const toast = useToast();
  const key = `dev:webhook-deliveries:${gameId}:${env}:${event.id}`;
  const deliveries = useQuery(key, () => api.webhookDeliveries(event.id), { refreshMs: 3000 });
  const redeliver = useAction((endpointId?: string) => api.redeliverWebhookEvent(event.id, endpointId));
  const list = deliveries.data?.data ?? [];

  const send = async (endpointId?: string) => {
    if (await redeliver.run(endpointId)) {
      invalidate(key);
      toast("Queued for delivery");
    }
  };

  return (
    <Modal open size="wide" title={event.type} description={<>Event <span className="mono">{event.id}</span> · {formatDateTime(event.created_at)}</>} onClose={onClose}
      footer={<>
        <Button onClick={onClose}>Close</Button>
        <Button variant="primary" busy={redeliver.pending} onClick={() => void send()}>Send again to all endpoints</Button>
      </>}>
      <div className="stack">
        <ErrorNotice error={deliveries.error ?? redeliver.error} />
        {deliveries.loading && !deliveries.data && <Loading />}
        {deliveries.data && list.length === 0 && (
          <div className="notice">No endpoint was subscribed when this event happened. Send it again to deliver it to your current endpoints.</div>
        )}
        {list.length > 0 && (
          <div className="table-wrap">
            <table className="data">
              <thead><tr><th>Endpoint</th><th>Outcome</th><th>Last attempt</th><th /></tr></thead>
              <tbody>
                {list.map((d) => (
                  <tr key={d.id}>
                    <td style={{ maxWidth: 220 }}><span className="truncate mono small" title={d.endpoint_url}>{d.endpoint_url}</span></td>
                    <td style={{ maxWidth: 260 }}>
                      <span className={`badge ${d.state === "delivered" ? "ok" : d.state === "failed" ? "bad" : ""}`}>{d.state}</span>
                      <div className="cell-sub clamp-2" title={deliveryText(d)}>{deliveryText(d)}</div>
                      {d.state === "pending" && d.next_attempt_at && d.attempts > 0 && <div className="cell-sub">next try {untilText(d.next_attempt_at, new Date().toISOString())}</div>}
                    </td>
                    <td>{relativeTime(d.last_attempt_at)}</td>
                    <td className="num nowrap">{d.state !== "delivering" && <Button size="small" onClick={() => void send(d.endpoint_id)}>Send again</Button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div>
          <div className="small" style={{ fontWeight: 500 }}>Payload</div>
          <pre className="json" style={{ maxHeight: 320 }}>{JSON.stringify(event, null, 2)}</pre>
        </div>
      </div>
    </Modal>
  );
}
